import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  HtmlEditor,
  Inject,
  Link,
  RichTextEditorComponent,
  Toolbar,
} from '@syncfusion/ej2-react-richtexteditor';

import { ReactComponent as FacebookIcon } from 'assets/icon-facebook.svg';
import { ReactComponent as YoutubeIcon } from 'assets/icon-youtube.svg';
import { ReactComponent as TwitterIcon } from 'assets/icon-twitter.svg';
import { ReactComponent as LinkedinIcon } from 'assets/icon-linkedin.svg';
import { ReactComponent as TextFieldIcon } from 'assets/icon-text-field.svg';
import SectionHeadline from 'shared/components/SectionHeadline';
import { LabelInput } from 'shared/components/Labels';
import CustomInput from 'shared/components/CustomInput';
import { colors } from 'shared/defaultTheme';
import { Switch } from 'shared/components/form/Inputs';

const IconContainer = styled.div`
  height: 20px;
  width: 20px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: 50%;
  padding-left: 4px;
  padding-right: 4px;
  svg {
    height: 100%;
    width: 100%;
  }
  margin-top: 4px;
`;

const SocialNetworksFooterSection = ({
  mainColor,
  showLogoFooter,
  setShowLogoFooter,
  youtubeLink,
  setYoutubeLink,
  twitterLink,
  setTwitterLink,
  facebookLink,
  setFacebookLink,
  linkedinLink,
  setLinkedinLink,
  footerText,
  setFooterText,
  phone,
  setPhone,
}) => {
  const { t } = useTranslation();
  const onChangeShowLogoFooter = () => {
    setShowLogoFooter(!showLogoFooter);
  };

  const onChangeFooterText = (event) => {
    setFooterText(event.value);
  };

  const onChange = (event, set) => {
    set(event.target.value);
  };

  return (
    <>
      <SectionHeadline
        className="general-customizer__section-headline-2"
        sectionNumber={2}
        label={t('general_customizer_section_social_networks')}
        color={mainColor}
      />
      <div className="general-customizer__text-editor-wrapper">
        <RichTextEditorComponent height={196} value={footerText} change={onChangeFooterText}>
          <Inject services={[Toolbar, HtmlEditor, Link]} />
        </RichTextEditorComponent>
      </div>
      <div className="general-customizer__social-network-wrapper">
        <LabelInput className="text-uppercase" label={t('general_youtube')} color={mainColor} />
        <CustomInput
          type="url"
          Icon={() => (
            <IconContainer backgroundColor={mainColor}>
              <YoutubeIcon fill={colors.white} />
            </IconContainer>
          )}
          value={youtubeLink}
          onChange={(event) => onChange(event, setYoutubeLink)}
        />
      </div>
      <div className="general-customizer__social-network-wrapper">
        <LabelInput className="text-uppercase" label={t('general_twitter')} color={mainColor} />
        <CustomInput
          type="url"
          Icon={() => (
            <IconContainer backgroundColor={mainColor}>
              <TwitterIcon fill={colors.white} />
            </IconContainer>
          )}
          value={twitterLink}
          onChange={(event) => onChange(event, setTwitterLink)}
        />
      </div>
      <div className="general-customizer__social-network-wrapper">
        <LabelInput className="text-uppercase" label={t('general_facebook')} color={mainColor} />
        <CustomInput
          type="url"
          Icon={() => (
            <IconContainer backgroundColor={mainColor}>
              <FacebookIcon fill={colors.white} />
            </IconContainer>
          )}
          value={facebookLink}
          onChange={(event) => onChange(event, setFacebookLink)}
        />
      </div>
      <div className="general-customizer__social-network-wrapper">
        <LabelInput className="text-uppercase" label={t('general_linkedin')} color={mainColor} />
        <CustomInput
          type="url"
          Icon={() => (
            <IconContainer backgroundColor={mainColor}>
              <LinkedinIcon fill={colors.white} />
            </IconContainer>
          )}
          value={linkedinLink}
          onChange={(event) => onChange(event, setLinkedinLink)}
        />
      </div>
      <div className="general-customizer__social-network-wrapper">
        <LabelInput className="text-uppercase" label={t('general_phone')} color={mainColor} />
        <CustomInput
          type="url"
          Icon={() => (
            <IconContainer backgroundColor="transparent">
              <TextFieldIcon fill={colors.suvaGrey} />
            </IconContainer>
          )}
          value={phone}
          onChange={(event) => onChange(event, setPhone)}
        />
      </div>
      <div className="general-customizer__switch-footer-logo-wrapper">
        <Switch
          className="text-uppercase"
          id="switchFooterLogo"
          label={t('general_customizer_show_logo_footer')}
          value={!showLogoFooter}
          color={showLogoFooter ? mainColor : colors.pattens}
          textColor={mainColor}
          onChange={onChangeShowLogoFooter}
        />
      </div>
    </>
  );
};

SocialNetworksFooterSection.defaultProps = {
  mainColor: '',
};

SocialNetworksFooterSection.propTypes = {
  mainColor: PropTypes.string,
  showLogoFooter: PropTypes.bool.isRequired,
  setShowLogoFooter: PropTypes.func.isRequired,
  youtubeLink: PropTypes.string.isRequired,
  setYoutubeLink: PropTypes.func.isRequired,
  twitterLink: PropTypes.string.isRequired,
  setTwitterLink: PropTypes.func.isRequired,
  facebookLink: PropTypes.string.isRequired,
  setFacebookLink: PropTypes.func.isRequired,
  linkedinLink: PropTypes.string.isRequired,
  setLinkedinLink: PropTypes.func.isRequired,
  footerText: PropTypes.string.isRequired,
  setFooterText: PropTypes.func.isRequired,
  phone: PropTypes.string.isRequired,
  setPhone: PropTypes.func.isRequired,
};

export default SocialNetworksFooterSection;
